import { DQ_PRO_PLAN_KEY, USER_ACCESS_LEVEL, LEARNCREATOR_START_PATH } from "../../../../utils/constants";
import { solutionPlanKey } from '../../../../utils/productAccess';
import { isDirectWorkspace } from '../../../../utils/capabilities';

/**
 * Getting-started step cards, keyed by plan_key so each DQ Pro plan can define
 * its own set. Only steps that carry a `key` participate in sequential
 * locking (see WorkspaceHome in DQProHome.page.jsx) — a step unlocks once the
 * previous step's `key` appears in authUser.onboarding_steps_completed. Steps
 * without a `key` (e.g. P1's bookend upgrade cards) keep the older
 * isUpgrade-only lock behavior.
 * Adding a new plan (p2/p3/p4) is just adding another array here.
 */
/**
 * Getting-started steps for CLIENT (level-22) educator workspaces — e.g. a school
 * lead created under an enterprise_p0 owner. Same card UI as ONBOARDING_STEPS_BY_PLAN
 * but scoped to the school: accounts are for *students* (their classes), and the
 * dashboard is the *school* dashboard (not the owner's partner/network dashboard).
 * Not sequentially locked — all steps are available to the educator.
 */
export const CLIENT_ONBOARDING_STEPS_BY_PLAN = {
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]: [
        {
            key: 1,
            stepNum: 'Step 1',
            title: 'Preview Assessments',
            desc: 'Preview the Child Online Safety Assessments for your students.',
            cta: 'View Assessments',
            path: '/dq_pro/products/cpci/suite',
        },
        {
            key: 2,
            stepNum: 'Step 2',
            title: 'Accounts Setup',
            desc: 'Create accounts for your students to take the Child Online Safety Assessments.',
            cta: 'Add Accounts',
            path: '/dq_pro/classes',
        },
        {
            key: 3,
            stepNum: 'Step 3',
            title: 'Track',
            desc: 'Visualize your school data and insights from the Child Online Safety Assessments.',
            cta: 'View Dashboard',
            path: '/dq_pro/school-dashboard/overview',
        },
        // Step 4 ("Deliver Learning") is P1-only — see the ENTERPRISE_P1 set below.
    ],

    // enterprise_p1: same educator journey as P0 PLUS Step 4 "Deliver Learning",
    // where the school requests a learning program from its DQ Partner and sees
    // the programs already linked to it (P1 Workspace flow, slides 158-160).
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]: [
        {
            key: 1,
            stepNum: 'Step 1',
            title: 'Preview Assessments',
            desc: 'Preview the Child Online Safety Assessments for your students.',
            cta: 'View Assessments',
            path: '/dq_pro/products/cpci/suite',
        },
        {
            key: 2,
            stepNum: 'Step 2',
            title: 'Accounts Setup',
            desc: 'Create accounts for your students to take the Child Online Safety Assessments.',
            cta: 'Add Accounts',
            path: '/dq_pro/classes',
        },
        {
            key: 3,
            stepNum: 'Step 3',
            title: 'Track',
            desc: 'Visualize your school data and insights from the Child Online Safety Assessments.',
            cta: 'View Dashboard',
            path: '/dq_pro/school-dashboard/overview',
        },
        // No `key`, so it stays OUTSIDE sequential locking (always available) —
        // it does not disturb the 1→2→3 educator lock flow.
        {
            stepNum: 'Step 4',
            title: 'Deliver Learning',
            desc: "Request a learning program tailored to your school's needs and assessment results, and see the programs already linked to your school.",
            cta: 'Get Learning Program',
            path: '/dq_pro/program/request',
        },
    ],
};

/**
 * Getting-started steps for a level-23 teacher (school-created, class-scoped
 * account), one set per plan (TW-LC-Home / TW-P1B-Home) — the teacher-home
 * mirror of pbdqPartnerHomeSteps below. Card text is identical across both
 * plans (same design family as ONBOARDING_STEPS_BY_PLAN.enterprise_p0, which
 * documents the "variants share identical card text" convention) — only
 * which steps sit behind an "Unlock" pill changes:
 *
 *  - TW-LC-Home:  Steps 2, 3 and 5 locked (an LC-only account has no
 *    assessment product, so Support Assessment / Review Insights stay
 *    behind Unlock; Step 4 — LearnCreator — is the one thing this plan has).
 *  - TW-P1B-Home: only Step 5 locked (P1B bundles the assessment product,
 *    so Steps 1-4 are all open).
 *
 * Step 5 is permanently locked on every variant — "Disable Step 5 unlock" —
 * via `unlockDisabled`, same as the PbDQ Partner Home's own Step 5.
 *
 * DELIBERATELY UNLINKED FOR NOW beyond Steps 1 and 4: no `path` on Steps 2, 3 and 5, so
 * their CTAs render but navigate nowhere (PlanStepCard no-ops a click when
 * `step.path` is unset) even once unlocked. Routing and capability-gating
 * are follow-up work — see docs/features/Teacher-Role-and-School-Workspace.md §7.
 */
const teacherHomeSteps = ({ lockedSteps = [] }) => {
    const locked = (n) => lockedSteps.includes(n);
    return [
        {
            stepNum: 'Step 1',
            title: 'Manage Your Classes',
            desc: 'Review the classes assigned to you so every student is ready to participate.',
            cta: 'View Classes',
            path: '/dq_pro/classes',
            isUpgrade: locked(1),
        },
        {
            stepNum: 'Step 2',
            title: 'Support Assessment',
            desc: 'Guide your students through the Child Online Safety Assessment and monitor participation in each class.',
            cta: 'View Assessment',
            isUpgrade: locked(2),
            /*
             * No teacher unlock flow exists yet: the Teacher deck has no
             * UnlockRequest slide (Gap Register G015) and BTN-1337 / BTN-1339
             * leave the route "TBD". A live pill opened the PARTNER's dialog
             * ("reviewed by DQ", the partner's catalogue) with a Continue that
             * sent nothing, so the pill stays drawn but dead, like Step 5,
             * until the reviewer and the entries are decided.
             */
            unlockDisabled: locked(2),
        },
        {
            stepNum: 'Step 3',
            title: 'Review Insights',
            desc: "Explore each class's assessment results against grade and school benchmarks.",
            cta: 'View Dashboard',
            isUpgrade: locked(3),
            // Same as Step 2 — BTN-1339, route TBD (G015).
            unlockDisabled: locked(3),
        },
        {
            stepNum: 'Step 4',
            title: 'Activate Targeted Learning',
            desc: 'Review learning recommendations and requests for your classes. Get the right programs or built with LearnCreator, and link them to your classes.',
            cta: 'Get Programs',
            // My Programs — the teacher's programs, linked to classes from there
            // (LA-TW-19Oct-03). No "1 new" pill: it was a fixed placeholder with no
            // real count behind it, so it showed when nothing was new.
            path: '/dq_pro/learning/program/list',
            isUpgrade: locked(4),
        },
        {
            stepNum: 'Step 5',
            title: 'Measure Improvement',
            desc: 'Request reassessment and compare results over time to see how readiness and risks are improving across your students.',
            cta: 'Request Reassessment',
            // Locked on every variant, and its pill is dead — see unlockDisabled.
            isUpgrade: true,
            unlockDisabled: true,
        },
    ];
};

export const TEACHER_ONBOARDING_STEPS_BY_PLAN = {
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]: teacherHomeSteps({ lockedSteps: [2, 3, 5] }),
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1B]: teacherHomeSteps({ lockedSteps: [5] }),
};

/**
 * The five PbDQ Partner Home cards, with a per-package lock set.
 *
 * All three package variants (PW-P0-Home, PW-P1-Home, PW-LC-Home — deck slides
 * 3, 4 and 5) draw THE SAME five cards with THE SAME copy. Only which of them
 * sit behind an "Unlock" pill changes, which is why this is one factory rather
 * than three near-identical arrays.
 *
 * Copy is taken verbatim from the slides.
 *
 * @param {number[]} lockedSteps  step numbers to render locked, e.g. [2, 3, 5]
 */
/**
 * The PbDQ School and School Network Home board — five cards, same design family
 * as pbdqPartnerHomeSteps below.
 *
 * Copy is VERBATIM from the workspace decks:
 *   School  — PbDQ_School Workspace_13Aug2026,  slides 3 (P0), 4 (P1), 5 (LC)
 *   Network — PbDQ_Network Workspace_13Aug2026, slides 2 (P0), 3 (P1), 4 (LC)
 *
 * The two workspaces share one board. Every card differs only in whether it says
 * "classes" or "schools", which is why this is one builder with an `isNetwork`
 * switch rather than two near-identical copies that would drift apart.
 *
 * WHAT THE LOCK MEANS. `isUpgrade` renders the card greyed with an "Unlock" pill,
 * exactly as on the Partner board. Which cards are locked is the PACKAGE, nothing
 * else — there is no sequential progress gate here, and deliberately no numeric
 * `key`: a key makes PlanStepCard derive `locked` from onboarding progress
 * instead, which would lock cards the plan says are open.
 *
 * STEP 5 is locked on every variant AND its pill is dead (`unlockDisabled`). All
 * six slides carry the same annotation — "Disable Step 5 unlock" — so the card
 * states the destination without offering a way to buy into it yet.
 *
 * @param {number[]} lockedSteps  step numbers to render behind an Unlock pill
 * @param {boolean}  isNetwork    true for SNW copy, false for SW
 */
const pbdqSchoolHomeSteps = ({ lockedSteps = [], isNetwork = false, step4Count = 0 }) => {
    const locked = (n) => lockedSteps.includes(n);
    // Step 4's "N new" pill counts the program suggestions DQ Pro has sent that
    // still wait for an answer, and shows only when there are some (bug sheet
    // Ian-SNW-29Sep-5). It used to be a fixed "1 new". Never on a locked card —
    // on P0 the card is locked, and the slide shows no pill.
    const step4Badge = (locked(4) || !(step4Count > 0)) ? undefined : `${step4Count} new`;

    return isNetwork ? [
        {
            stepNum: 'Step 1',
            title: 'Set Up Your Schools',
            desc: 'Add and manage schools in your network so every school is ready to participate.',
            cta: 'Add Schools',
            path: '/dq_pro/clients/list',
            isUpgrade: locked(1),
        },
        {
            stepNum: 'Step 2',
            title: 'Launch Assessment',
            desc: 'Link the Child Online Safety Assessment to your schools and monitor participation across your schools.',
            cta: 'Link Assessment',
            // CS-Solution-P0-overview — Solutions › COS Children Assessment ›
            // Overview (bug sheet Ian-SNW-29Sep-5; it used to open My Schools).
            path: '/dq_pro/solutions/children-assessment/overview',
            isUpgrade: locked(2),
        },
        {
            stepNum: 'Step 3',
            title: 'Review Insights',
            desc: 'Explore assessment results for your schools against national and global benchmarks.',
            cta: 'View Dashboard',
            // Row 96 names a School Network Dashboard — placeholder page for now,
            // same destination the SNW sidebar's Insights item uses.
            path: '/dq_pro/school-network-dashboard',
            isUpgrade: locked(3),
        },
        {
            stepNum: 'Step 4',
            title: 'Activate Targeted Learning',
            desc: 'Review learning recommendations and requests for your schools. Get the right programs from your partner or built with LearnCreator, and share them across your schools.',
            topBadge: step4Badge,
            cta: 'Get Programs',
            path: '/dq_pro/learning/program/list',
            isUpgrade: locked(4),
        },
        {
            stepNum: 'Step 5',
            title: 'Measure Improvement',
            desc: 'Request reassessment and compare results over time to see how readiness and risks are improving across your schools.',
            cta: 'Request Reassessment',
            isUpgrade: true,
            unlockDisabled: true,
        },
    ] : [
        {
            stepNum: 'Step 1',
            title: 'Set Up Your Classes',
            desc: 'Add classes, assign teachers, and enroll learners so every student is ready to participate.',
            cta: 'Add Classes',
            path: '/dq_pro/classes',
            isUpgrade: locked(1),
        },
        {
            stepNum: 'Step 2',
            title: 'Launch Assessment',
            desc: 'Link the Child Online Safety Assessment to your classes and monitor participation across your school.',
            cta: 'Link Assessment',
            // CS-Solution-P0-overview — Solutions › COS Children Assessment ›
            // Overview (bug sheet Ian-SW-29Sep-2; it used to open My Classes).
            path: '/dq_pro/solutions/children-assessment/overview',
            isUpgrade: locked(2),
        },
        {
            stepNum: 'Step 3',
            title: 'Review Insights',
            desc: 'Explore assessment results for your school and classes against national and global benchmarks.',
            cta: 'View Dashboard',
            path: '/dq_pro/school-dashboard/overview',
            isUpgrade: locked(3),
        },
        {
            stepNum: 'Step 4',
            title: 'Activate Targeted Learning',
            desc: 'Review learning recommendations and requests for your school. Get the right programs from your partner or built with LearnCreator, and link them to your classes.',
            topBadge: step4Badge,
            cta: 'Get Programs',
            path: '/dq_pro/learning/program/list',
            isUpgrade: locked(4),
        },
        {
            stepNum: 'Step 5',
            title: 'Measure Improvement',
            desc: 'Request reassessment and compare results over time to see how readiness and risks are improving across your school.',
            cta: 'Request Reassessment',
            isUpgrade: true,
            unlockDisabled: true,
        },
    ];
};

/**
 * Which cards each package leaves open, counted off the Unlock pills on the
 * slides. School and Network lock identically for the same plan.
 *
 *   P0            Steps 1-3 open · 4 and 5 locked
 *   P1 / P1A/P1B  Steps 1-4 open · 5 locked
 *   LC            Step 1 and 4 open · 2, 3 and 5 locked
 *
 * LC is the mirror image of P0, exactly as on the Partner board: no COS
 * assessment, so Launch Assessment and Review Insights are both behind an
 * Unlock, while Targeted Learning is the one thing that plan has.
 */
const PBDQ_SCHOOL_LOCKS = {
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]:  [4, 5],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]:  [5],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1A]: [5],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1B]: [5],
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]:  [2, 3, 5],
};

/**
 * The Home board for a client workspace — a school, or a school network.
 *
 * Returns null for a plan the PbDQ decks say nothing about (default, dq_pro,
 * educator, enterprise, p2/p3/p4). The caller falls back to
 * CLIENT_ONBOARDING_STEPS_BY_PLAN for those, so existing non-PbDQ customers keep
 * exactly the board they have today.
 *
 * @param {string}  planKey   the workspace's dq_pro_plan.plan_key
 * @param {boolean} isNetwork authUser.is_school_network
 * @param {number}  [step4Count] pending DQ Pro suggestions — Step 4's pill
 */
export const pbdqClientHomeSteps = (planKey, isNetwork, step4Count = 0) => {
    // A direct plan (a school or network buying for itself) gets its solution's board.
    const lockedSteps = PBDQ_SCHOOL_LOCKS[solutionPlanKey(planKey)];
    if (!lockedSteps) return null;
    return pbdqSchoolHomeSteps({ lockedSteps, isNetwork: !!isNetwork, step4Count });
};

/** Marks the Partner Home card whose pill counts the Pipeline's waiting requests. */
const PIPELINE_WAITING = 'pipeline_waiting';

/**
 * The Partner Home's Step 3 pill — "N waiting", the requests raised by
 * networks, schools and teachers that wait for the partner to answer (the
 * Pipeline's Open tab, source school_request: its "Awaiting Response" rows).
 * Shown only when there are some, and never on a locked card (LC locks Step 3).
 *
 * @param {object[]} steps   a Partner Home board (ONBOARDING_STEPS_BY_PLAN)
 * @param {number}   waiting the count, from the program-requests list
 */
export const withPipelineWaitingBadge = (steps, waiting) => steps.map((s) => (s.badgeSource === PIPELINE_WAITING
    ? { ...s, topBadge: (!s.isUpgrade && waiting > 0) ? `${waiting} waiting` : undefined }
    : s));

/** Whether this board has the Pipeline-fed pill at all — so only it fetches the count. */
export const hasPipelineWaitingBadge = (steps) => Array.isArray(steps)
    && steps.some((s) => s.badgeSource === PIPELINE_WAITING && !s.isUpgrade);

const pbdqPartnerHomeSteps = ({ lockedSteps = [] }) => {
    const locked = (n) => lockedSteps.includes(n);
    return [
        {
            stepNum: 'Step 1',
            title: 'Set Up Accounts',
            desc: 'Create and manage school network and school accounts to prepare for participation.',
            cta: 'Set Up Accounts',
            path: '/dq_pro/clients/list',
            isUpgrade: locked(1),
        },
        {
            stepNum: 'Step 2',
            title: 'Launch Assessment',
            desc: 'Preview the Child Online Safety Assessment, publish it to your networks and schools, and monitor participation.',
            cta: 'Launch Assessment',
            // Lands on Solutions › COS Children Assessment › Overview (LA-PW-16Oct-01),
            // where the partner previews it and links it to schools.
            path: '/dq_pro/solutions/children-assessment/overview',
            isUpgrade: locked(2),
        },
        {
            stepNum: 'Step 3',
            title: 'Review Opportunities',
            desc: 'Explore assessment results, identify priority risks and readiness gaps, and view requests from networks, schools, and teachers.',
            // The slide's red count pill: requests from networks, schools and
            // teachers waiting for the partner — the Pipeline's "Awaiting
            // Response" rows. It was a fixed "3 waiting"; the Home page fills it
            // in with withPipelineWaitingBadge.
            badgeSource: PIPELINE_WAITING,
            // Same destination as P0 — the pipeline page, not the program-requests
            // list, which admits enterprise_p1 only.
            ctas: [
                { label: 'View Opportunities', path: '/dq_pro/growth-opportunities/pipeline' },
            ],
            isUpgrade: locked(3),
        },
        {
            stepNum: 'Step 4',
            title: 'Create & Enable Learning',
            desc: 'Create and distribute targeted learning programs, or unlock LearnCreator so networks, schools, and teachers can build programs aligned with their curricula and students\' needs.',
            ownerOnly: true,
            /**
             * Opens LearnCreator. It does NOT activate a product.
             *
             * Button Registry, PW-LC-Home and PW-P1-Home Step 4: action "Open
             * LearnCreator.", destination PW-P1, LC-LearnCreator. Only P0's row
             * differs, and that one is disabled behind the Unlock pill anyway
             * ("Keep disabled because LearnCreator is not active in P0").
             *
             * This used to be { action: 'activate_product' }, carried over from
             * the old DQ Pro card set. On an LC or P1 account the learning-program
             * product is ALREADY enabled, so isCtaActivated returned true and the
             * button rendered as a disabled "Activated" state with a green dot —
             * exactly the one card those plans are supposed to be able to open.
             */
            cta: 'Open LearnCreator',
            // The LearnCreator landing page every entry point opens (bug sheet
            // Ian-LC-30Sep-1). It pointed at /dq_pro/learncreator, a "Coming
            // soon" placeholder.
            path: LEARNCREATOR_START_PATH,
            isUpgrade: locked(4),
        },
        {
            stepNum: 'Step 5',
            title: 'Measure Improvement',
            desc: 'Reassess participating schools and compare learning gains and risk reduction over time.',
            // Locked on every variant, and its pill is dead — see unlockDisabled.
            isUpgrade: true,
            unlockDisabled: true,
            ownerOnly: true,
            cta: 'Reassess Schools',
        },
    ];
};

export const ONBOARDING_STEPS_BY_PLAN = {
    /**
     * enterprise_p0 — the PbDQ Partner Workspace Home (PW-P0-Home, deck slide 3).
     *
     * Steps 4 and 5 locked; 1-3 open. Straight from the Button Registry rows for
     * PW-P0-Home: Set Up Accounts, Launch Assessment and View Opportunities all
     * carry live destinations, while Step 4 is "Keep disabled because LearnCreator
     * is not active in P0" and Step 5 is locked on every variant.
     *
     * NO SEQUENTIAL LOCKING. This used to carry key: 1/2/3, which made Step 2 stay
     * locked until Step 1 was marked complete in onboarding_steps_completed. That
     * meant two partners on the SAME plan saw different boards depending on how far
     * through onboarding they happened to be — a fresh P0 account got only Step 1,
     * while one with progress got Steps 1-3. The design has no such gate: the lock
     * is the package, nothing else.
     *
     * Dropping the keys makes planHasSequentialSteps false for this plan, so the
     * complete_onboarding_step writes no-op at their existing guards. Level-22
     * client cards keep their own sequential flow in
     * CLIENT_ONBOARDING_STEPS_BY_PLAN, which is untouched.
     */
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]: pbdqPartnerHomeSteps({ lockedSteps: [4, 5] }),

    /**
     * enterprise_p1a / enterprise_p1b — the COS Readiness Solution, in its
     * Provider-Created and Teacher-Created variants (PW-P1-Home, deck slide 4).
     *
     * Same five cards as P0. Per the Button Registry's PW-P1-Home rows, Steps 1-4
     * are all open here — Launch Assessment and View Opportunities are live, and
     * Step 4 opens LearnCreator rather than offering an unlock. Only Step 5 is
     * locked, as it is on every variant.
     *
     * Both plans share this set. The design treats P1 as one Home; Gap Register
     * G051 records that P1A and P1B differ materially and that making Step 4
     * entitlement-aware is still an open decision, so no split is invented here.
     *
     * No numeric `key` on any card, deliberately: a key makes PlanStepCard derive
     * `locked` from onboarding progress instead of the package, which would lock
     * cards the package says are open.
     */
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1A]: pbdqPartnerHomeSteps({ lockedSteps: [5] }),
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1B]: pbdqPartnerHomeSteps({ lockedSteps: [5] }),

    /**
     * enterprise_lc — LearnCreator only (PW-LC-Home, deck slide 5).
     *
     * The mirror image of P0. The Button Registry's PW-LC-Home rows say Launch
     * Assessment and View Opportunities are both disabled behind an Unlock —
     * "Keep disabled because COS assessment is not active in LC-only access" —
     * while Step 4 opens LearnCreator, which is the one thing this plan has.
     */
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]: pbdqPartnerHomeSteps({ lockedSteps: [2, 3, 5] }),

    /**
     * enterprise_p1 — the COS Readiness Solution (PW-P1-Home, deck slide 4).
     *
     * Navigation Bar rows 4-6 give PW_HO "Home" exactly three Solution Types:
     * P0, P1 and LC. The deck matches with PW-P0-Home, PW-P1-Home and
     * PW-LC-Home. So enterprise_p1 is the PbDQ P1 board, not a separate product.
     *
     * Steps 1-4 open, Step 5 locked — per the Button Registry PW-P1-Home rows,
     * where Launch Assessment, View Opportunities and Open LearnCreator all
     * carry live destinations.
     *
     * REPLACES the Workforce journey that used to sit on this key (Define /
     * Setup / Baseline / Build / Track / Certify). That content is preserved
     * below as WORKFORCE_ONBOARDING_STEPS rather than deleted, so it can be
     * put back on a key of its own if that product is still wanted.
     */
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]: pbdqPartnerHomeSteps({ lockedSteps: [5] }),
};

/**
 * The former enterprise_p1 "Learning Tracker" / Workforce journey.
 *
 * UNWIRED. enterprise_p1 now serves the PbDQ Partner Home, which the Navigation
 * Bar sheet and the Partner Workspace deck both define for that solution type.
 * Kept verbatim so the journey is recoverable: give it its own plan key and add
 * it back to ONBOARDING_STEPS_BY_PLAN.
 */
export const WORKFORCE_ONBOARDING_STEPS = [
        {
            stepNum: 'Step 0',
            title: 'Define',
            desc: "Configure your organization's competency model (domains, proficiency levels, and what AI-literate means for each role) against 100+ global frameworks.",
            isUpgrade: true,
            cta: 'Create Framework',
        },
        {
            stepNum: 'Step 1',
            title: 'Setup',
            desc: 'Add accounts for your team members and employees by department and roles.',
            cta: 'Add Accounts',
            path: '/dq_pro/clients/list',
        },
        {
            stepNum: 'Step 2',
            title: 'Baseline',
            desc: 'Deploy custom assessments across the workforce to establish a baseline and identify gaps.',
            cta: 'Build Assessment',
            path: '/dq_pro/products/assessments',
        },
        {
            stepNum: 'Step 3',
            title: 'Build',
            desc: 'Create and distribute AI-digital literacy courses to your team based on gaps you identified.',
            // Single orange CTA. The school-requests entry point lives as its own
            // card on the Create page (slide 180), not as a second button here.
            cta: 'Build Course',
            path: '/dq_pro/program/creation/preview',
        },
        {
            stepNum: 'Step 4',
            title: 'Track',
            desc: 'Monitor employee capabilities by individuals, departments, and organization.',
            cta: 'View Dashboard',
            path: '/dq_pro/insights',
        },
        {
            stepNum: 'Step 5',
            title: 'Certify',
            desc: 'Submit your program for independent validation against global standards. DQ Stars awards a star rating and certification mark.',
            isUpgrade: true,
            cta: 'Get Certified',
        },
];


/**
 * Whether this plan_key has sequentially-locked step cards (i.e. steps with a
 * numeric `key`) at all. Guards every complete_onboarding_step call site —
 * calling it for a plan with no numbered steps (or no plan match) would be a
 * meaningless write, so callers should no-op instead. Generalizes beyond a
 * single hardcoded plan_key so a future plan (p2/p3/p4) with numbered steps is
 * automatically covered without touching the call sites.
 */
export const planHasSequentialSteps = (planKey) => {
    const steps = ONBOARDING_STEPS_BY_PLAN[planKey];
    return Array.isArray(steps) && steps.some(step => step.key !== undefined);
};

/**
 * Gate for the client-side complete_onboarding_step writes — mirror of
 * planHasSequentialSteps for the level-22 client path. Two checks: the user is a
 * level-22 client — or a direct workspace's owner, which gets the school board — AND the
 * plan (direct plans read as their solution) defines a CLIENT step set (currently P0).
 */
export const isClientOnboardingUser = (authUser) =>
    (authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE || isDirectWorkspace(authUser))
    && CLIENT_ONBOARDING_STEPS_BY_PLAN[solutionPlanKey(authUser?.dq_pro_plan?.plan_key)] !== undefined;

/**
 * Merge an onboarding-progress refresh into the CURRENT session user.
 *
 * After a complete_onboarding_step write, pages re-fetch /user/details to pick
 * up the new onboarding_steps_completed. That refresh must NOT replace the
 * whole authUser: session fields captured at login (e.g. the dq_pro_plan a
 * level-22 client inherits from the workspace owner, product_access, branding)
 * can come back different or empty from a bare details fetch, and replacing
 * the object silently downgrades the session — e.g. the free-plan route guard
 * starts sending /dq_pro/classes to /not-found. Only the onboarding progress
 * is taken from the refreshed payload.
 *
 * Returns the SAME authUser reference when there is nothing to merge, so
 * callers can skip setAuthUser (and the localStorage rewrite) in that case.
 */
export const mergeOnboardingRefreshIntoAuthUser = (currentAuthUser, refreshedUser) => {
    if (!currentAuthUser) return refreshedUser || null;
    if (!refreshedUser || refreshedUser.onboarding_steps_completed === undefined) return currentAuthUser;
    return { ...currentAuthUser, onboarding_steps_completed: refreshedUser.onboarding_steps_completed };
};
